<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <ReflectionIcon kind="team" size="title" />
                <h2 class="title-subsection text-ink">{{ t("reflections.team.title") }}</h2>
                <TabInfoToggle :expanded="infoOpen" :topic="t('reflections.team.tab.infoTopic')" @toggle="infoOpen = !infoOpen" />
            </div>
            <p class="body-small text-ink-muted">{{ t("reflections.team.description") }}</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <BaseDialog v-model:open="infoOpen" :title="t('reflections.team.tab.infoTitle')">
            <HowTeamReflectionsWork />
        </BaseDialog>

        <div v-if="requests.length" class="flex flex-col gap-2">
            <div class="scaffold-details-small text-ink-muted">{{ dueLabel }}</div>
            <TeamRequestRow v-for="req in requests" :key="req.invite_token" :request="req" @give-feedback="emit('give-feedback', $event)" />
        </div>
        <ReflectionsEmptyState v-else kind="team" :title="t('reflections.team.tab.emptyTitle')">
            {{ t("reflections.team.tab.emptyBody") }}
        </ReflectionsEmptyState>
    </section>
</template>

<script setup lang="ts">
import type { TeamReflectionRequest } from "~vue/api/generated/reflections";
import BaseDialog from "~vue/components/BaseDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import HowTeamReflectionsWork from "~vue/pages/reflections/HowTeamReflectionsWork.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import ReflectionsEmptyState from "~vue/pages/reflections/ReflectionsEmptyState.vue";
import TabInfoToggle from "~vue/pages/reflections/TabInfoToggle.vue";
import TeamRequestRow from "~vue/pages/reflections/TeamRequestRow.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    requests: TeamReflectionRequest[];
}

const props = defineProps<Props>();
const { t } = useI18n();
const emit = defineEmits<{ (event: "give-feedback", request: TeamReflectionRequest): void }>();

const infoOpen = ref(false);

const dueLabel = computed(() => {
    const withClose = props.requests.filter((r) => r.closes_at);
    if (!withClose.length) {
        return t("reflections.team.tab.due");
    }
    const earliest = withClose.reduce((acc, r) => {
        if (!acc) {
            return r.closes_at as string;
        }
        return new Date(r.closes_at as string) < new Date(acc) ? (r.closes_at as string) : acc;
    }, withClose[0].closes_at as string);
    const days = Math.round((new Date(earliest).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days <= 0) {
        return t("reflections.team.tab.dueToday");
    }
    if (days === 1) {
        return t("reflections.team.tab.dueTomorrow");
    }
    if (days <= 7) {
        return t("reflections.team.tab.dueInDays", { days }, days);
    }
    const short = formatDate(earliest, "short");
    return short ? t("reflections.team.tab.dueOn", { date: short }) : t("reflections.team.tab.due");
});
</script>
